import { createSelector } from 'reselect';

import {
  EnrichedWalletAsset,
  SpotProduct,
} from 'components/SpotTradingIndia/Modal/types';
import { CURRENCY_TYPE } from 'constants/enums';
import IState from 'types/Istore';
import { IBalance } from 'types/IWallet';

import { assetSelector, spotContractTypeProductsSelector } from './tradeSelectors';
import { spotWalletBalanceList } from './walletSelector';

const spotStateSelector = (state: IState) => state.spot;

export const isSpotModalOpenSelector = createSelector(
  [spotStateSelector],
  spotState => spotState?.isSpotModalOpen
);

/**
 * Selector to get the current panel mode
 */
export const panelModeSelector = createSelector(
  [spotStateSelector],
  spotState => spotState?.panelMode
);

/**
 * Selector to check if we came from trade panel
 */
export const cameFromTradeSelector = createSelector(
  [spotStateSelector],
  spotState => spotState?.cameFromTrade || false
);

/**
 * Selector to check if asset changes are disabled (during trade or order status modal)
 */
export const isAssetChangeDisabledSelector = createSelector(
  [spotStateSelector],
  spotState => spotState?.isAssetChangeDisabled || false
);

/**
 * Selector to get the transfer amount (pre-filled from trade shortfall)
 */
export const transferAmountSelector = createSelector(
  [spotStateSelector],
  spotState => spotState?.transferAmount || ''
);

export const spotWalletLiveListSelector = createSelector(
  [spotWalletBalanceList, spotContractTypeProductsSelector, assetSelector],
  (spotWalletList, spotProducts, assets): EnrichedWalletAsset[] => {
    if (!spotWalletList || spotWalletList.length === 0) {
      return [];
    }

    return spotWalletList
      ?.map((walletAsset: IBalance) => {
        const assetData = assets?.[walletAsset.asset_id] ?? walletAsset?.asset;
        const { symbol = '', precision = 0, name = '' } = assetData ?? {};
        const assetBalance = Number(walletAsset?.balance || 0);
        const isINR = symbol === CURRENCY_TYPE.INR;

        // Check if this asset exists in spot products (INR is always shown)
        const foundSpotProduct = isINR
          ? undefined
          : (spotProducts as SpotProduct[])?.find(
              (product: SpotProduct) =>
                product.underlying_asset?.symbol === symbol &&
                product.quoting_asset?.symbol === 'INR'
            );

        const assetExistsInSpotProducts = isINR || foundSpotProduct !== undefined;

        // Format quantity display
        const formattedQuantity =
          assetBalance > 0
            ? assetBalance.toFixed(
                foundSpotProduct?.product_specs?.underlying_precision || precision
              )
            : '0';

        const shouldShowQuantity = assetBalance > 0 && symbol !== CURRENCY_TYPE.INR;

        return {
          asset: assetData,
          name,
          balance: String(walletAsset?.balance),
          symbol,
          assetBalance,
          assetExistsInSpotProducts,
          formattedQuantity,
          shouldShowQuantity,
          isINR,
          spotProduct: foundSpotProduct,
          precision: foundSpotProduct?.product_specs?.underlying_precision || precision,
        };
      })
      ?.sort((a, b) => {
        const priorityA =
          (a.spotProduct as SpotProduct)?.ui_config?.sort_priority ??
          Number.MAX_SAFE_INTEGER;
        const priorityB =
          (b.spotProduct as SpotProduct)?.ui_config?.sort_priority ??
          Number.MAX_SAFE_INTEGER;
        return priorityA - priorityB;
      });
  }
);
